import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate } from "@/lib/format";
import { X, FileCsv, Printer, BookOpen } from "@phosphor-icons/react";
import useBranches from "@/hooks/useBranches";
import { useAuth } from "@/context/AuthContext";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function firstDayOfMonthISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

const METHOD_ORDER = ["cash", "upi", "card", "online", "bank", "cheque"];

function MethodBreakdown({ totals }) {
  const inMap = totals.receipts_by_method || {};
  const outMap = totals.payments_by_method || {};
  // Only show methods that actually have activity, in canonical order.
  const usedMethods = METHOD_ORDER.filter(
    (m) => (inMap[m] || 0) > 0 || (outMap[m] || 0) > 0,
  );
  if (usedMethods.length === 0) return null;
  return (
    <div
      className="border border-canvas-line bg-canvas-panel/40"
      data-testid="cashbook-method-breakdown"
    >
      <div className="px-3 py-2 border-b border-canvas-line font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
        Method Breakdown
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-canvas-line bg-canvas-panel">
            <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              Method
            </th>
            <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              Receipts In
            </th>
            <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              Payments Out
            </th>
            <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              Net
            </th>
          </tr>
        </thead>
        <tbody>
          {usedMethods.map((m) => {
            const inAmt = inMap[m] || 0;
            const outAmt = outMap[m] || 0;
            const net = inAmt - outAmt;
            return (
              <tr
                key={m}
                data-testid={`cashbook-method-${m}`}
                className="border-b border-canvas-line last:border-b-0"
              >
                <td className="px-3 py-1.5 font-mono text-[11px] uppercase">{m}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-[#00B341]">
                  {inAmt > 0 ? `+${fmtMoney(inAmt)}` : "—"}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-destructive">
                  {outAmt > 0 ? `−${fmtMoney(outAmt)}` : "—"}
                </td>
                <td
                  className={`px-3 py-1.5 text-right font-mono tabular-nums font-semibold ${
                    net > 0 ? "text-[#00B341]" : net < 0 ? "text-destructive" : "text-ink"
                  }`}
                >
                  {fmtMoney(net)}
                </td>
              </tr>
            );
          })}
          <tr className="bg-canvas-panel">
            <td className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider font-semibold">
              Total
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums font-semibold text-[#00B341]">
              {fmtMoney(totals.receipts)}
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums font-semibold text-destructive">
              {fmtMoney(totals.payments)}
            </td>
            <td
              className={`px-3 py-1.5 text-right font-mono tabular-nums font-bold ${
                totals.net > 0 ? "text-[#00B341]" : totals.net < 0 ? "text-destructive" : "text-ink"
              }`}
            >
              {fmtMoney(totals.net)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function downloadCsvViaAuthedFetch(url, filename) {
  // The CSV endpoint requires the bearer cookie; reuse axios so withCredentials sticks.
  return fetch(url, { credentials: "include" })
    .then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = filename;
      document.body.appendChild(dl);
      dl.click();
      dl.remove();
    });
}

function printCashbook(book) {
  const w = window.open("", "_blank", "width=960,height=1200");
  if (!w) return;
  const esc = (s) =>
    String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");
  const dayBlocks = book.days
    .map((day) => {
      const rows = day.entries
        .map(
          (e) => `<tr>
        <td style="font-family:'IBM Plex Mono',monospace;font-size:11px">${esc(e.voucher_no)}</td>
        <td>${esc(e.party_name)} <span style="color:#5C5C5C;font-size:11px">(${esc(e.party_type)})</span></td>
        <td style="text-transform:uppercase;font-family:'IBM Plex Mono',monospace;font-size:11px">${esc(e.payment_method)}</td>
        <td style="font-family:'IBM Plex Mono',monospace;font-size:11px">${esc(e.reference || "—")}</td>
        <td style="font-size:11px;color:#333">${esc(e.narration || "—")}</td>
        <td style="text-align:right;color:#00713a;font-family:'IBM Plex Mono',monospace">${e.amount_in ? esc(fmtMoney(e.amount_in)) : ""}</td>
        <td style="text-align:right;color:#a30000;font-family:'IBM Plex Mono',monospace">${e.amount_out ? esc(fmtMoney(e.amount_out)) : ""}</td>
      </tr>`,
        )
        .join("");
      return `<div class="day">
        <h3>${esc(fmtDate(day.date))} <span class="muted">· ${day.entries.length} entries</span></h3>
        <table>
          <thead><tr><th>Voucher</th><th>Party</th><th>Method</th><th>Reference</th><th>Narration</th><th style="text-align:right">In (₹)</th><th style="text-align:right">Out (₹)</th></tr></thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr class="day-total">
              <td colspan="5" style="text-align:right;font-weight:700">Day total · running balance ${esc(fmtMoney(day.running_balance))}</td>
              <td style="text-align:right;font-weight:700;color:#00713a">${esc(fmtMoney(day.daily_in))}</td>
              <td style="text-align:right;font-weight:700;color:#a30000">${esc(fmtMoney(day.daily_out))}</td>
            </tr>
          </tfoot>
        </table>
      </div>`;
    })
    .join("");

  const methods = ["cash", "upi", "card", "online", "bank", "cheque"];
  const inMap = book.totals.receipts_by_method || {};
  const outMap = book.totals.payments_by_method || {};
  const usedMethods = methods.filter((m) => (inMap[m] || 0) > 0 || (outMap[m] || 0) > 0);
  const methodBlock = usedMethods.length === 0
    ? ""
    : `<div class="card"><h3>Method Breakdown</h3>
        <table>
          <thead><tr><th>Method</th><th style="text-align:right">Receipts In</th><th style="text-align:right">Payments Out</th><th style="text-align:right">Net</th></tr></thead>
          <tbody>
            ${usedMethods.map((m) => {
              const inn = inMap[m] || 0;
              const ot = outMap[m] || 0;
              const net = inn - ot;
              const color = net > 0 ? "#00713a" : net < 0 ? "#a30000" : "#0A0A0A";
              return `<tr>
                <td style="text-transform:uppercase;font-family:'IBM Plex Mono',monospace;font-size:11px">${esc(m)}</td>
                <td style="text-align:right;color:#00713a;font-family:'IBM Plex Mono',monospace">${inn ? esc(fmtMoney(inn)) : "—"}</td>
                <td style="text-align:right;color:#a30000;font-family:'IBM Plex Mono',monospace">${ot ? esc(fmtMoney(ot)) : "—"}</td>
                <td style="text-align:right;font-weight:600;color:${color};font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(net))}</td>
              </tr>`;
            }).join("")}
            <tr style="background:#fafafa;border-top:2px solid #0A0A0A">
              <td style="font-family:'IBM Plex Mono',monospace;font-size:11px;font-weight:700">TOTAL</td>
              <td style="text-align:right;color:#00713a;font-weight:700;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(book.totals.receipts))}</td>
              <td style="text-align:right;color:#a30000;font-weight:700;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(book.totals.payments))}</td>
              <td style="text-align:right;font-weight:700;font-family:'IBM Plex Mono',monospace">${esc(fmtMoney(book.totals.net))}</td>
            </tr>
          </tbody>
        </table>
      </div>`;

  w.document.write(`<!doctype html><html><head><title>Cashbook ${esc(book.branch.code || book.branch.id)} ${esc(book.period.from)} → ${esc(book.period.to)}</title>
  <style>
    *{box-sizing:border-box}
    body{font-family:'Work Sans',Helvetica,sans-serif;color:#0A0A0A;margin:24px;background:#fff}
    h1{font-family:'Cabinet Grotesk',sans-serif;font-size:22px;margin:0}
    h3{font-family:'Cabinet Grotesk',sans-serif;font-size:14px;margin:0 0 8px}
    .eyebrow{font-family:'IBM Plex Mono',monospace;font-size:10px;text-transform:uppercase;letter-spacing:.3em;color:#5C5C5C;margin-top:6px}
    .muted{color:#5C5C5C;font-weight:400}
    .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:18px 0}
    .summary div{border:1px solid #0A0A0A;padding:10px}
    .summary .l{font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:#5C5C5C}
    .summary .v{font-family:'Cabinet Grotesk',sans-serif;font-size:18px;font-weight:700;margin-top:4px}
    .card{margin-top:16px;border:1px solid #0A0A0A;padding:12px}
    .day{margin-top:16px;border:1px solid #0A0A0A;padding:0}
    .day h3{margin:0;padding:8px 12px;background:#F4F4F4;font-family:'Cabinet Grotesk',sans-serif;font-size:14px;border-bottom:1px solid #0A0A0A}
    table{width:100%;border-collapse:collapse}
    th,td{text-align:left;padding:6px 10px;border-bottom:1px solid #eee;font-size:12px}
    th{font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#5C5C5C;background:#fafafa}
    tfoot td{background:#fafafa;border-top:2px solid #0A0A0A}
    .foot{margin-top:24px;display:flex;justify-content:space-between;font-size:11px;font-family:'IBM Plex Mono',monospace;color:#5C5C5C;text-transform:uppercase;letter-spacing:.15em}
    @media print{button{display:none}}
  </style></head><body>
  <h1>Cashbook · ${esc(book.branch.name || book.branch.id)}</h1>
  <div class="eyebrow">${esc(book.branch.code || book.branch.id)} · ${esc(book.period.from)} → ${esc(book.period.to)}</div>

  <div class="summary">
    <div><div class="l">Total Receipts</div><div class="v" style="color:#00713a">${esc(fmtMoney(book.totals.receipts))}</div></div>
    <div><div class="l">Total Payments</div><div class="v" style="color:#a30000">${esc(fmtMoney(book.totals.payments))}</div></div>
  </div>

  ${methodBlock}

  ${book.days.length === 0
    ? `<div style="border:1px dashed #ccc;padding:30px;text-align:center;color:#5C5C5C;font-family:'IBM Plex Mono',monospace;font-size:11px">No vouchers in this period.</div>`
    : dayBlocks}

  <div class="foot"><span>${esc(book.totals.count)} entries · net ${esc(fmtMoney(book.totals.net))}</span><span>Generated ${esc(new Date().toLocaleString())}</span></div>
  <script>window.onload=function(){window.print();}</script>
  </body></html>`);
  w.document.close();
}

export default function CashbookExportDialog({ open, onClose }) {
  const { user } = useAuth();
  const { branches } = useBranches({ activeOnly: true });

  const [branchId, setBranchId] = useState("");
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setBook(null);
      return;
    }
    // Default to user's branch (staff) or the first active branch (admin).
    if (!branchId) {
      if (user?.branch_id) setBranchId(user.branch_id);
      else if (branches.length > 0) setBranchId(branches[0].id);
    }
  }, [open, user, branches, branchId]);

  const branchName = useMemo(
    () => branches.find((b) => b.id === branchId)?.name || branchId,
    [branches, branchId],
  );

  const preview = async () => {
    if (!branchId) {
      toast.error("Pick a branch.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/vouchers/cashbook/report", {
        params: { branch_id: branchId, date_from: dateFrom, date_to: dateTo },
      });
      setBook(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    if (!branchId) {
      toast.error("Pick a branch.");
      return;
    }
    try {
      const url = `${API}/vouchers/cashbook/csv?branch_id=${encodeURIComponent(branchId)}&date_from=${dateFrom}&date_to=${dateTo}`;
      const branchCode = branches.find((b) => b.id === branchId)?.code || branchId;
      await downloadCsvViaAuthedFetch(
        url,
        `cashbook_${branchCode}_${dateFrom}_${dateTo}.csv`,
      );
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  const exportPdf = async () => {
    if (!book) await preview();
    if (book) {
      printCashbook(book);
    } else {
      // preview() above sets book asynchronously; user can re-click.
      toast.info("Preview loaded — click 'Print / PDF' again to render.");
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="cashbook-dialog"
        className="bg-canvas-card border-2 border-ink w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted">
              Cashbook Export
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1 flex items-center gap-2">
              <BookOpen size={18} weight="bold" />
              Daily Running Balance · {branchName || "—"}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="close-cashbook-dialog"
            className="p-2 border border-canvas-line hover:border-ink"
            aria-label="Close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Branch
              </label>
              <select
                value={branchId}
                onChange={(e) => {
                  setBranchId(e.target.value);
                  setBook(null);
                }}
                data-testid="cashbook-branch"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              >
                <option value="" disabled>
                  Select a branch…
                </option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setBook(null);
                }}
                data-testid="cashbook-from"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setBook(null);
                }}
                data-testid="cashbook-to"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={preview}
              disabled={loading}
              data-testid="cashbook-preview"
              className="px-3 py-2 border border-ink text-ink text-xs font-semibold hover:bg-ink hover:text-white disabled:opacity-60"
            >
              {loading ? "Loading…" : "Preview"}
            </button>
            <button
              type="button"
              onClick={exportCsv}
              data-testid="cashbook-export-csv"
              className="px-3 py-2 border border-ink text-ink text-xs font-semibold hover:bg-ink hover:text-white flex items-center gap-1.5"
            >
              <FileCsv size={12} weight="bold" />
              Download CSV
            </button>
            <button
              type="button"
              onClick={exportPdf}
              disabled={!book}
              data-testid="cashbook-export-pdf"
              className="px-3 py-2 bg-ink text-white text-xs font-semibold hover:bg-brand transition-colors disabled:opacity-60 flex items-center gap-1.5"
            >
              <Printer size={12} weight="bold" />
              Print / PDF
            </button>
          </div>

          {book && (
            <div data-testid="cashbook-preview-pane" className="space-y-3">
              <div className="grid grid-cols-2 gap-2 font-mono text-[11px]">
                <div className="border border-[#00B341] p-2 bg-[#00B341]/5">
                  <div className="text-[9px] uppercase tracking-wider text-ink-muted">Receipts</div>
                  <div className="font-semibold text-[#00B341] mt-0.5">{fmtMoney(book.totals.receipts)}</div>
                </div>
                <div className="border border-destructive p-2 bg-destructive/5">
                  <div className="text-[9px] uppercase tracking-wider text-ink-muted">Payments</div>
                  <div className="font-semibold text-destructive mt-0.5">{fmtMoney(book.totals.payments)}</div>
                </div>
              </div>

              <MethodBreakdown totals={book.totals} />

              {book.days.length === 0 && (
                <div className="border border-dashed border-canvas-line p-6 text-center font-mono text-xs text-ink-subtle">
                  No vouchers in this period.
                </div>
              )}

              {book.days.map((day) => (
                <div
                  key={day.date}
                  data-testid={`cashbook-day-${day.date}`}
                  className="border border-canvas-line"
                >
                  <div className="px-3 py-2 bg-canvas-panel border-b border-canvas-line flex items-center justify-between">
                    <div className="font-mono text-[11px] font-semibold">
                      {fmtDate(day.date)}
                      <span className="text-ink-subtle ml-1">· {day.entries.length} entries</span>
                    </div>
                    <div className="font-mono text-[11px]">
                      <span className="text-[#00B341]">+{fmtMoney(day.daily_in)}</span>
                      <span className="mx-2 text-ink-subtle">/</span>
                      <span className="text-destructive">−{fmtMoney(day.daily_out)}</span>
                      <span className="ml-3 font-semibold">
                        Run: {fmtMoney(day.running_balance)}
                      </span>
                    </div>
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {day.entries.map((e, i) => (
                        <tr
                          key={`${day.date}-${i}`}
                          className="border-b border-canvas-line last:border-b-0"
                        >
                          <td className="px-3 py-1.5 font-mono text-[10px] font-semibold">
                            {e.voucher_no}
                          </td>
                          <td className="px-3 py-1.5">{e.party_name}</td>
                          <td className="px-3 py-1.5 font-mono text-[10px] uppercase">
                            {e.payment_method}
                          </td>
                          <td className="px-3 py-1.5 font-mono text-[10px] text-ink-muted">
                            {e.reference || "—"}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono text-[#00B341] tabular-nums">
                            {e.amount_in ? `+${fmtMoney(e.amount_in)}` : ""}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono text-destructive tabular-nums">
                            {e.amount_out ? `−${fmtMoney(e.amount_out)}` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
